import type { ReactNode } from 'react'
import { useMemo } from 'react'

import { buildMetadata } from './BuildMetadata'
import { useIdleReload } from './hooks/useIdleReload'
import { useVersionPolling } from './hooks/useVersionPolling'
import { createTelemetrySink } from './telemetry'
import { VersionContext } from './VersionContext'
import { createVersionService } from './VersionService'

/**
 * Liga o `VersionService` à árvore React — e é a única peça desta feature que
 * conhece React.
 *
 * Substitui o `DeployRefreshManager`, que fazia tudo dentro de um hook: fetch,
 * comparação e `location.reload()` sem intermediários. Aqui o provider só
 * monta, injeta o sink de telemetria e delega; toda decisão vem do serviço,
 * que é testável sem DOM.
 */
/**
 * Escotilhas de teste, lidas em build time e ausentes em produção.
 *
 * Sem elas o serviço é inalcançável de ponta a ponta: em dev ele nasce
 * desligado, e a janela de inatividade real é de 60s — um E2E que a respeitasse
 * levaria mais de um minuto por cenário. O mecanismo anterior não tinha nada
 * disso e por isso nunca foi exercitado fora do teste unitário da função pura;
 * o defeito que derrubou produção morava justamente no que ficou de fora.
 *
 * Em produção `VITE_VERSION_*` não existe e os defaults valem: desligado em
 * dev, janela de 60s. Ver `tests/e2e/version-update.spec.ts`.
 */
const testOverrides = {
  enabled: import.meta.env.VITE_VERSION_SERVICE_ENABLED === 'true',
  idleWindowMs: Number(import.meta.env.VITE_VERSION_IDLE_MS) || undefined,
  pollIntervalMs: Number(import.meta.env.VITE_VERSION_POLL_MS) || undefined
}

export const VersionProvider = ({ children }: { children: ReactNode }) => {
  const service = useMemo(
    () =>
      createVersionService({
        currentVersion: buildMetadata.version,
        telemetry: createTelemetrySink(),
        // Em desenvolvimento quem atualiza o app é o HMR — mesmo gate que o
        // mecanismo anterior tinha.
        enabled: !import.meta.env.DEV || testOverrides.enabled,
        idleWindowMs: testOverrides.idleWindowMs,
        pollIntervalMs: testOverrides.pollIntervalMs
      }),
    []
  )

  useVersionPolling(service)
  useIdleReload(service)

  return (
    <VersionContext.Provider value={service}>{children}</VersionContext.Provider>
  )
}
